import { Icon } from "@iconify/react/dist/iconify.js";
import { useCallback, useEffect, useState, useRef } from "react";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import Shimmer from "./ui/Shimmer";
import { TextWrap } from "./ui/TextWrap";
import { usePermissions } from "../context/PermissionContext";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const TaxTypesLayer = () => {
  const [taxTypes, setTaxTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState(""); // 'add', 'edit', or 'delete'
  const [showModal, setShowModal] = useState(false);
  const [taxTypeId, setTaxTypeId] = useState(null);
  const [taxType, setTaxType] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);
  const [selectedTaxType, setSelectedTaxType] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [modalLoading, setModalLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [error, setError] = useState(null);
  const { hasPermission } = usePermissions();

  // Ref for debouncing search
  const searchTimeoutRef = useRef(null);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Fetch tax types from API
  const fetchTaxTypes = useCallback(
    async (
      page = 1,
      perPageParam = 10,
      search = "",
      sortField = null,
      sortDirection = "asc"
    ) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiCall("get", "/get-all-tax-types", {
          search,
          per_page: perPageParam,
          want_pagination: 1,
          sort_by: sortField || "name",
          sort_order: sortDirection,
          page,
        });

        const apiData = response.data || response;
        const paginatedData = apiData.data || [];

        setTaxTypes(paginatedData);
        setTotalEntries(apiData.total || 0);
        setTotalPages(apiData.last_page || 1);
        setCurrentPage(apiData.current_page || 1);
        const handleRes = response.data;
        handleApiResponse(handleRes, "Success", "GET");
      } catch (error) {
        const handleErr = error.response?.data;
        handleApiResponse(handleErr, "Error", "GET");
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Proper debounced search function
  const debouncedSearch = useCallback(
    (page, perPageParam, search) => {
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout
      searchTimeoutRef.current = setTimeout(() => {
        fetchTaxTypes(
          page,
          perPageParam,
          search,
          sortConfig.field,
          sortConfig.direction
        );
      }, 500);
    },
    [fetchTaxTypes, sortConfig.field, sortConfig.direction]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    debouncedSearch(1, perPage, value);
  };

  const handleSort = (field) => {
    const newDirection =
      sortConfig.field === field && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({
      field,
      direction: newDirection,
    });

    // Immediately fetch with new sort
    fetchTaxTypes(currentPage, perPage, searchQuery, field, newDirection);
  };

  // Initial load
  

  // Handle page changes
  useEffect(() => {
    fetchTaxTypes(
      currentPage,
      perPage,
      searchQuery,
      sortConfig.field,
      sortConfig.direction
    );
  }, [currentPage]);

  // Handle per page changes
  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
    fetchTaxTypes(
      1,
      value,
      searchQuery,
      sortConfig.field,
      sortConfig.direction
    );
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown")) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOpenModal = async (type, taxTypeData = null) => {
    setModalType(type);
    setShowModal(true);
    setTaxTypeId(null);
    setTaxType("");
    setDescription("");
    setActive(true);
    setSelectedTaxType(null);
    setModalLoading(false);

    if (type === "edit" && taxTypeData) {
      setModalLoading(true);
      try {
        // Simulate loading or make API call if needed
        setTimeout(() => {
          setTaxTypeId(taxTypeData.id);
          setTaxType(taxTypeData.name);
          setDescription(taxTypeData.description || "");
          setActive(
            taxTypeData.active !== undefined ? taxTypeData.active : true
          );
          setSelectedTaxType(taxTypeData);
          setModalLoading(false);
        }, 1000);
      } catch (error) {
        handleCustomResponse("error", "Failed to load tax type data.");
        setModalLoading(false);
      }
    } else if (type === "delete" && taxTypeData) {
      setTaxTypeId(taxTypeData.id);
      setSelectedTaxType(taxTypeData);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType("");
    setTaxTypeId(null);
    setTaxType("");
    setDescription("");
    setActive(true);
    setSelectedTaxType(null);
    setModalLoading(false);
  };

  const handleAddTaxType = async () => {
    if (!taxType) {
      handleCustomResponse("error", "Tax type is required.");
      return;
    }
    if (description.length > 255) {
      handleCustomResponse(
        "error",
        "Description cannot exceed 255 characters."
      );
      return;
    }

    setLoading(true);
    try {
      const response = await apiCall("post", "/add-tax-type", {
        name: taxType,
        description: description,
      });
      const handleRes = response.data;
      handleApiResponse(handleRes, "success", "POST");

      fetchTaxTypes(
        currentPage,
        perPage,
        searchQuery,
        sortConfig.field,
        sortConfig.direction
      );
      handleCloseModal();
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "POST");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTaxType = async () => {
    if (!taxType) {
      handleCustomResponse("error", "Tax type is required.");
      return;
    }
    if (!taxTypeId) {
      handleCustomResponse("error", "Invalid or missing Tax Type ID.");
      return;
    }
    if (description.length > 255) {
      handleCustomResponse(
        "error",
        "Description cannot exceed 255 characters."
      );
      return;
    }

    setLoading(true);
    try {
      const response = await apiCall("put", `/update-tax-type/${taxTypeId}`, {
        name: taxType,
        description,
      });
      const handleRes = response.data;
      handleApiResponse(handleRes, "success", "PUT");
      fetchTaxTypes(
        currentPage,
        perPage,
        searchQuery,
        sortConfig.field,
        sortConfig.direction
      );
      handleCloseModal();
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "PUT");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTaxType = async () => {
    if (!taxTypeId) {
      handleCustomResponse("error", "Invalid or missing Tax Type ID.");
      return;
    }

    setLoading(true);
    try {
      const response = await apiCall("delete", `/delete-tax-type/${taxTypeId}`);
      const handleRes = response.data;
      handleApiResponse(handleRes, "success", "DELETE");
      fetchTaxTypes(
        currentPage,
        perPage,
        searchQuery,
        sortConfig.field,
        sortConfig.direction
      );
      handleCloseModal();
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "DELETE");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (modalType === "add") {
      handleAddTaxType();
    } else if (modalType === "edit") {
      handleUpdateTaxType();
    } else if (modalType === "delete") {
      handleDeleteTaxType();
    }
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= 255) {
      setDescription(value);
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const startEntry = totalEntries > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endEntry = Math.min(currentPage * perPage, totalEntries);
  if (error) {
    return <ErrorAlert />;
  }

  return (
    <div className="p-4 p-md-3 p-sm-2">
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by tax type"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={perPage}
              handle906
              handlePerPageChange={handlePerPageChange}
            />
            <div className="ms-auto">
              {hasPermission("insert_tax_types") && (
                <button
                  className="btn btn-primary-600 btn-sm d-flex align-items-center"
                  onClick={() => handleOpenModal("add")}
                >
                  <Icon icon="ri:add-line" className="me-1" /> Add Tax Type
                </button>
              )}
            </div>
          </div>

          {taxTypes.length === 0 && !loading ? (
            <TableDataNoAvailable message="No tax Types found." />
          ) : (
            <div className="position-relative">
              <div
                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                <div className="table-responsive">
                  <table className="table bordered-table mb-0 table-hover">
                    <thead>
                      <tr
                        style={{ backgroundColor: "#475EAB", color: "white" }}
                      >
                        <th scope="col" className="p-2">
                          S.No
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("name")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Tax Type</span>
                            <SortIcons field="name" sortConfig={sortConfig} />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("description")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Description</span>
                            <SortIcons
                              field="description"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("created_at")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Created Date</span>
                            <SortIcons
                              field="created_at"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th scope="col" className="p-2">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {taxTypes.map((taxTypeItem, index) => (
                        <tr key={taxTypeItem.id}>
                          <td className="p-2">
                            {(currentPage - 1) * perPage + index + 1}
                          </td>
                          <td className="p-2">
                            <span className="">
                              {taxTypeItem.name}
                            </span>
                          </td>
                          <td className="p-2">
                            <TextWrap
                              location={
                                taxTypeItem.description || "No description"
                              }
                            />
                          </td>
                          <td className="p-2">
                            <span>{formatDate(taxTypeItem.created_at)}</span>
                          </td>
                          <td className="p-2">
                            {hasPermission("update_tax_types") && (
                              <button
                                className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                onClick={() =>
                                  handleOpenModal("edit", taxTypeItem)
                                }
                                style={{
                                  position: "relative",
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "scale(1.1)";
                                  const tooltip = document.createElement("div");
                                  tooltip.textContent = "Edit";
                                  tooltip.style.cssText = `
                                  position: absolute;
                                  top: -35px;
                                  left: 50%;
                                  transform: translateX(-50%);
                                  background-color: #333;
                                  color: white;
                                  padding: 5px 8px;
                                  border-radius: 4px;
                                  font-size: 12px;
                                  white-space: nowrap;
                                  z-index: 1000;
                                  pointer-events: none;
                                `;
                                  tooltip.id = "edit-tooltip";
                                  e.currentTarget.appendChild(tooltip);
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                  const tooltip =
                                    document.getElementById("edit-tooltip");
                                  if (tooltip) tooltip.remove();
                                }}
                              >
                                <Icon icon="lucide:edit" />
                              </button>
                            )}

                            {hasPermission("delete_tax_types") && (
                              <button
                                className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                onClick={() =>
                                  handleOpenModal("delete", taxTypeItem)
                                }
                                style={{
                                  position: "relative",
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "scale(1.1)";
                                  const tooltip = document.createElement("div");
                                  tooltip.textContent = "Delete";
                                  tooltip.style.cssText = `
                                  position: absolute;
                                  top: -35px;
                                  left: 50%;
                                  transform: translateX(-50%);
                                  background-color: #333;
                                  color: white;
                                  padding: 5px 8px;
                                  border-radius: 4px;
                                  font-size: 12px;
                                  white-space: nowrap;
                                  z-index: 1000;
                                  pointer-events: none;
                                `;
                                  tooltip.id = "delete-tooltip";
                                  e.currentTarget.appendChild(tooltip);
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                  const tooltip =
                                    document.getElementById("delete-tooltip");
                                  if (tooltip) tooltip.remove();
                                }}
                              >
                                <Icon icon="mingcute:delete-2-line" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{
                    zIndex: 10,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <LoadingComponent />
                </div>
              )}

              <PaginationControls
                pagination={{
                  currentPage,
                  perPage,
                  total: totalEntries,
                }}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      <div
        className={`modal fade ${showModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {modalType === "add"
                  ? "Add New Tax Type"
                  : modalType === "edit"
                  ? "Edit Tax Type"
                  : "Delete Tax Type"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
              ></button>
            </div>
            <div className="modal-body">
              {modalType === "delete" ? (
                <p>
                  Are you sure you want to delete{" "}
                  <strong>{selectedTaxType?.name || "this tax type"}</strong>?
                </p>
              ) : modalLoading ? (
                <Shimmer type="modal" />
              ) : (
                <>
                  <div className="mb-3">
                    <label htmlFor="taxType" className="form-label">
                      Tax Type
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="taxType"
                      value={taxType}
                      onChange={(e) => setTaxType(e.target.value)}
                      placeholder="Enter tax type"
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">
                      Description
                    </label>
                    <textarea
                      className="form-control radius-8"
                      id="description"
                      value={description}
                      onChange={handleDescriptionChange}
                      placeholder="Enter description"
                      rows="3"
                      maxLength={255}
                    ></textarea>
                    <small className="form-text text-muted">
                      {description.length}/255 characters
                    </small>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${
                  modalType === "delete" ? "btn-danger" : "btn-primary-600"
                }`}
                onClick={handleSubmit}
                disabled={loading || modalLoading}
              >
                {modalType === "add"
                  ? loading
                    ? "Please wait..."
                    : "Add Tax Type"
                  : modalType === "edit"
                  ? loading
                    ? "Please wait..."
                    : "Update Tax Type"
                  : loading
                  ? "Please wait..."
                  : "Delete Tax Type"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxTypesLayer;
